/**
 * Vorsortieren: nach Gruppen filtern und genau diese Gruppe an- oder abwaehlen.
 *
 * 🔑 Der Sinn ist nicht das Filtern an sich, sondern die **Entscheidung in einem
 * Griff**. Bei 241 uhlsport-Artikeln ist „alle ausser denen ohne Bild" von Hand eine
 * halbe Stunde Klickarbeit — und genau solche Auswahlen will man treffen, bevor
 * geschrieben wird.
 *
 * Die Gruppen sind bewusst dieselben wie die Ampeln in der Liste (Existenz, Kategorie,
 * Bild). Zwei Begriffe fuer dieselbe Sache waeren eine Fehlerquelle: wer „ohne Bild"
 * filtert, muss genau die Zeilen sehen, die in der Liste „ohne Bild" tragen.
 *
 * 🔴 „Existiert bereits" laesst sich filtern, aber nicht auswaehlen. Die Regel steht
 * ueber dem Klick (§7): ein Artikel, dessen EAN schon in Plenty liegt, wird nicht
 * angelegt — auch nicht, wenn jemand ihn anhakt.
 */
import type { Ampel } from '../lib/typen';
import { zahl } from '../lib/format';

/**
 * Steht dieser Artikel schon in Plenty?
 *
 * 🔴 **Bewusst ein eigener Name, nicht `status`** (Fix 22.09.2026). Vorschau und
 * Entwurf fuehren beide ein Feld `status` — aber mit verschiedenen Vokabularen:
 * die Vorschau den Existenzbefund (`neu`/`existiert`/`ungeprueft`), der Entwurf den
 * Zeilenzustand (`bereit`/`uebersprungen`/`fehler`). Die Entwurfsseite reichte ihren
 * Zeilenzustand hierher durch, und weil `bereit` auf keine Gruppe passte, zeigte die
 * Leiste **„Anlegen 0 · Unklar 88"** — obwohl genau diese 88 die anlegbaren waren.
 *
 * Ein eigener Feldname zwingt jeden Aufrufer zu einer bewussten Umrechnung; mit
 * `status` sah der falsche Aufruf aus wie der richtige.
 */
export type ExistenzBefund = 'neu' | 'existiert' | 'ungeprueft';

/** Ein Artikel, so weit die Vorsortierung ihn braucht — Vorschau wie Entwurf. */
export interface SortierbarerArtikel {
  schluessel: string;
  existenz: ExistenzBefund;
  ampelKategorie: Ampel | null;
  ampelBild: Ampel | null;
}

export type GruppenSchluessel =
  | 'alle' | 'neu' | 'existiert' | 'ungeprueft'
  | 'ohneBild' | 'bilderTeilweise' | 'ohneKategorie';

interface Gruppe {
  schluessel: GruppenSchluessel;
  name: string;
  erklaerung: string;
  /** false = diese Gruppe kann nicht angelegt werden, nur angesehen. */
  waehlbar: boolean;
  trifft: (a: SortierbarerArtikel) => boolean;
}

export const GRUPPEN: Gruppe[] = [
  {
    schluessel: 'alle',
    name: 'Alle',
    erklaerung: 'Keine Einschränkung.',
    waehlbar: true,
    trifft: () => true,
  },
  {
    schluessel: 'neu',
    name: 'Anlegen',
    erklaerung: 'Keine EAN dieses Artikels steht in Plenty — er würde neu angelegt.',
    waehlbar: true,
    trifft: a => a.existenz === 'neu',
  },
  {
    schluessel: 'existiert',
    name: 'Existiert schon',
    erklaerung: 'Mindestens eine EAN liegt schon in Plenty. Diese Artikel werden nie '
      + 'angelegt — für sie ist die Betriebsart „Felder aktualisieren" da.',
    waehlbar: false,
    trifft: a => a.existenz === 'existiert',
  },
  {
    schluessel: 'ungeprueft',
    name: 'Unklar',
    erklaerung: 'Ohne EAN konnte nicht gegen den Bestand geprüft werden. „Unklar" ist '
      + 'nicht „neu" — hier wurde nachgesehen und nichts gefunden, worauf man sich '
      + 'stützen könnte.',
    waehlbar: true,
    trifft: a => a.existenz === 'ungeprueft',
  },
  {
    schluessel: 'ohneBild',
    name: 'Ohne Bild',
    erklaerung: 'Keine Farbe dieses Artikels hat ein Foto im gewählten Ordner. Blockiert '
      + 'nicht — „Bilder nachliefern" holt sie später, auch aus einem anderen Ordner.',
    waehlbar: true,
    trifft: a => a.ampelBild === 'rot',
  },
  {
    schluessel: 'bilderTeilweise',
    name: 'Bilder unvollständig',
    erklaerung: 'Ein Teil der Farben hat ein Foto, ein Teil nicht.',
    waehlbar: true,
    trifft: a => a.ampelBild === 'gelb',
  },
  {
    schluessel: 'ohneKategorie',
    name: 'Ohne Kategorie',
    erklaerung: 'Keine Plenty-Kategorie zugeordnet. Der Artikel entsteht trotzdem — das '
      + 'Tool legt selbst keine Kategorien an.',
    waehlbar: true,
    trifft: a => a.ampelKategorie === 'rot',
  },
];

/** Wendet einen Gruppenfilter an. Pure — deshalb hier und nicht in der Seite. */
export function filtere<T extends SortierbarerArtikel>(
  artikel: T[],
  gruppe: GruppenSchluessel,
): T[] {
  const treffer = GRUPPEN.find(g => g.schluessel === gruppe);
  return treffer ? artikel.filter(treffer.trifft) : artikel;
}

/** Zaehlt je Gruppe. Pure. */
export function zaehle(artikel: SortierbarerArtikel[]): Record<GruppenSchluessel, number> {
  const stand = {} as Record<GruppenSchluessel, number>;
  for (const g of GRUPPEN) stand[g.schluessel] = artikel.filter(g.trifft).length;
  return stand;
}

export interface VorsortierungProps {
  artikel: SortierbarerArtikel[];
  aktiv: GruppenSchluessel;
  aufGruppe: (gruppe: GruppenSchluessel) => void;
  /**
   * Sammelauswahl. Fehlt sie (Vorschau), gibt es nur den Filter — in der Vorschau
   * ist noch nichts auszuwählen.
   */
  aufAuswahl?: (schluessel: string[], ausgewaehlt: boolean) => void;
  /** Blockiert die Sammelauswahl, solange etwas laeuft. */
  beschaeftigt?: boolean;
}

export default function Vorsortierung({
  artikel, aktiv, aufGruppe, aufAuswahl, beschaeftigt = false,
}: VorsortierungProps) {
  const stand = zaehle(artikel);
  const aktiveGruppe = GRUPPEN.find(g => g.schluessel === aktiv) ?? GRUPPEN[0];
  const betroffen = filtere(artikel, aktiv);

  return (
    <div className="space-y-2 rounded border border-[#DAD9D9] bg-[#F7F7F5] p-3">
      <div className="flex flex-wrap gap-2">
        {GRUPPEN.map(g => {
          const anzahl = stand[g.schluessel];
          const istAktiv = g.schluessel === aktiv;
          return (
            <button
              key={g.schluessel}
              type="button"
              title={g.erklaerung}
              onClick={() => aufGruppe(g.schluessel)}
              disabled={anzahl === 0 && g.schluessel !== 'alle'}
              className={`rounded border px-2.5 py-1 text-xs transition-colors ${
                istAktiv
                  ? 'border-[#1A1A1A] bg-[#E9FF57] text-[#1A1A1A]'
                  : anzahl === 0
                    ? 'border-[#DAD9D9] bg-white text-[#1A1A1A]/35'
                    : 'border-[#DAD9D9] bg-white text-[#1A1A1A] hover:border-[#1A1A1A]'
              }`}
            >
              {g.name} <span className="tabular-nums font-medium">{zahl(anzahl)}</span>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-[#1A1A1A]/60">{aktiveGruppe.erklaerung}</p>

      {aufAuswahl && (
        <div className="flex flex-wrap items-center gap-2 border-t border-[#DAD9D9] pt-2">
          <span className="text-xs text-[#1A1A1A]/60">
            {aktiv === 'alle'
              ? 'Für alle Artikel:'
              : `Für die ${zahl(betroffen.length)} Artikel dieser Gruppe:`}
          </span>
          <button
            type="button"
            disabled={beschaeftigt || !aktiveGruppe.waehlbar || betroffen.length === 0}
            onClick={() => aufAuswahl(betroffen.map(a => a.schluessel), true)}
            className="rounded border border-[#1A1A1A] bg-[#E9FF57] px-2.5 py-1 text-xs text-[#1A1A1A] hover:bg-[#d4ea4d] disabled:opacity-40"
          >
            auswählen
          </button>
          <button
            type="button"
            disabled={beschaeftigt || betroffen.length === 0}
            onClick={() => aufAuswahl(betroffen.map(a => a.schluessel), false)}
            className="rounded border border-[#DAD9D9] bg-white px-2.5 py-1 text-xs text-[#1A1A1A] hover:border-[#1A1A1A] disabled:opacity-40"
          >
            abwählen
          </button>
          {!aktiveGruppe.waehlbar && (
            <span className="text-xs text-[#1A1A1A]/60">
              — diese Gruppe lässt sich nicht anlegen, nur abwählen.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
